import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  TableSortLabel,
  Button,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  TablePagination,
} from "@mui/material";
import axios from "axios";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { fr } from "date-fns/locale";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

interface Vote {
  _id: string;
  voterNumber: string;
  match: {
    _id: string;
    url: string;
    competition: string;
    date: string;
    location: string | null;
    pool: string;
    scoreTeam1: number;
    scoreTeam2: number;
    season: string;
    status: string;
    team1: string;
    team2: string;
  };
  player: {
    _id: string;
    match: string;
    number: number;
    team: string;
    name: string;
  };
  competition: {
    _id: string;
    fffId: string;
    category: string;
    name: string;
    season: string;
    url: string;
    lastUpdated: string;
  };
  playerNumber?: number;
  playerName: string;
  directVote: boolean;
  isCoachVote: boolean;
  date: Date;
  isActive: boolean;
  paymentStatus: string;
  currency: string;
  paymentAmount?: number;
  paymentUrl?: string;
  stripeSessionId?: string;
  paidAt?: Date;
  stripePaymentIntentId?: string;
  timestamp: Date;
}

type SortKey =
  | "playerName"
  | "playerNumber"
  | "matchDate"
  | "date"
  | "voterNumber"
  | "isActive"
  | "isCoachVote"
  | "paymentStatus";
type SortDirection = "asc" | "desc";

const VotesDashboard: React.FC = () => {
  const [votes, setVotes] = useState<Vote[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState<{
    key: SortKey;
    direction: SortDirection;
  }>({ key: "matchDate", direction: "desc" });

  const [voteDateFilter, setVoteDateFilter] = useState<Date | null>(null);
  const [matchDateFilter, setMatchDateFilter] = useState<Date | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [coachVoteFilter, setCoachVoteFilter] = useState<string>("all");

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { token, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchVotes = async () => {
      try {
        if (!token) {
          toast.error("Vous devez vous connecter pour accéder à cette page");
          navigate("/login");
          return;
        }

        const response = await axios.get("/api/votes", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setVotes(response.data.data);
      } catch (error: any) {
        console.error("Error fetching votes:", error);
        if (error.response?.status === 401) {
          toast.error("Session expirée, veuillez vous reconnecter");
          logout();
          navigate("/login");
        } else {
          toast.error("Erreur lors du chargement des votes");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchVotes();
  }, [token, logout, navigate]);

  const handleSort = (key: SortKey) => {
    let direction: SortDirection = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedVotes = React.useMemo(() => {
    let filtered = [...votes];

    // Apply date filters
    if (voteDateFilter) {
      filtered = filtered.filter((v) => {
        const voteDate = new Date(v.date);
        return voteDate.toDateString() === voteDateFilter.toDateString();
      });
    }

    if (matchDateFilter) {
      filtered = filtered.filter((v) => {
        const matchDate = new Date(v.match.date);
        return matchDate.toDateString() === matchDateFilter.toDateString();
      });
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((v) => {
        if (statusFilter === "active") return v.isActive;
        if (statusFilter === "inactive") return !v.isActive;
        return true;
      });
    }

    // Apply coach vote filter
    if (coachVoteFilter !== "all") {
      filtered = filtered.filter((v) => {
        if (coachVoteFilter === "coach") return v.isCoachVote;
        if (coachVoteFilter === "player") return !v.isCoachVote;
        return true;
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        if (sortConfig.key === "matchDate") {
          const dateA = new Date(a.match.date).getTime();
          const dateB = new Date(b.match.date).getTime();
          return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
        }

        if (sortConfig.key === "date") {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
        }

        if (a[sortConfig.key] === undefined) return 1;
        if (b[sortConfig.key] === undefined) return -1;

        if (
          sortConfig.key === "playerName" ||
          sortConfig.key === "voterNumber" ||
          sortConfig.key === "paymentStatus"
        ) {
          const valueA = String(a[sortConfig.key]).toLowerCase();
          const valueB = String(b[sortConfig.key]).toLowerCase();
          return sortConfig.direction === "asc"
            ? valueA.localeCompare(valueB)
            : valueB.localeCompare(valueA);
        }

        if (sortConfig.key === "playerNumber") {
          const numA = a.playerNumber || 0;
          const numB = b.playerNumber || 0;
          return sortConfig.direction === "asc" ? numA - numB : numB - numA;
        }

        if (sortConfig.key === "isActive") {
          const valueA = a.isActive ? 1 : 0;
          const valueB = b.isActive ? 1 : 0;
          return sortConfig.direction === "asc"
            ? valueA - valueB
            : valueB - valueA;
        }

        if (sortConfig.key === "isCoachVote") {
          const valueA = a.isCoachVote ? 1 : 0;
          const valueB = b.isCoachVote ? 1 : 0;
          return sortConfig.direction === "asc"
            ? valueA - valueB
            : valueB - valueA;
        }

        return 0;
      });
    }

    return filtered;
  }, [
    votes,
    sortConfig,
    voteDateFilter,
    matchDateFilter,
    statusFilter,
    coachVoteFilter,
  ]);

  // Get paginated data
  const paginatedVotes = React.useMemo(() => {
    const startIndex = page * rowsPerPage;
    return sortedVotes.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedVotes, page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: Date | string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const resetFilters = () => {
    setVoteDateFilter(null);
    setMatchDateFilter(null);
    setStatusFilter("all");
    setCoachVoteFilter("all");
    setPage(0);
  };

  return (
    <Box>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ mb: 4, color: "#2c3e50", fontWeight: "bold" }}
      >
        Gestion des Votes
      </Typography>

      {/* Statistics Cards */}
      <Box sx={{ display: "flex", gap: 2, mb: 4, flexWrap: "wrap" }}>
        <Card sx={{ flex: 1, minWidth: 200 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total des votes
            </Typography>
            <Typography variant="h4" component="div" color="primary">
              {votes.length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, minWidth: 200 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Votes actifs
            </Typography>
            <Typography variant="h4" component="div" color="success.main">
              {votes.filter((v) => v.isActive).length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, minWidth: 200 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Votes inactifs
            </Typography>
            <Typography variant="h4" component="div" color="error.main">
              {votes.filter((v) => !v.isActive).length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, minWidth: 200 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Votes filtrés
            </Typography>
            <Typography variant="h4" component="div" color="secondary">
              {sortedVotes.length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, minWidth: 200 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Votes Coach
            </Typography>
            <Typography variant="h4" component="div" color="info.main">
              {votes.filter((v) => v.isCoachVote).length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, minWidth: 200 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Votes Joueur
            </Typography>
            <Typography variant="h4" component="div" color="warning.main">
              {votes.filter((v) => !v.isCoachVote).length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Filtres
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "flex-start",
            }}
          >
            <DatePicker
              label="Filtrer par date du vote"
              value={voteDateFilter}
              onChange={(newDate) => setVoteDateFilter(newDate)}
              slotProps={{ textField: { sx: { minWidth: 200 } } }}
            />
            <DatePicker
              label="Filtrer par date du match"
              value={matchDateFilter}
              onChange={(newDate) => setMatchDateFilter(newDate)}
              slotProps={{ textField: { sx: { minWidth: 200 } } }}
            />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Statut</InputLabel>
              <Select
                value={statusFilter}
                label="Statut"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="active">Actifs</MenuItem>
                <MenuItem value="inactive">Inactifs</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Type de vote</InputLabel>
              <Select
                value={coachVoteFilter}
                label="Type de vote"
                onChange={(e) => setCoachVoteFilter(e.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="coach">Coach</MenuItem>
                <MenuItem value="player">Public</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              onClick={resetFilters}
              sx={{
                textTransform: "none",
                height: "fit-content",
                alignSelf: "center",
              }}
            >
              Réinitialiser les filtres
            </Button>
          </Box>
        </LocalizationProvider>
      </Paper>

      {/* Votes Table */}
      <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : votes.length === 0 ? (
          <Typography variant="body1" align="center" p={4}>
            Aucun vote enregistré
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    <TableSortLabel
                      active={sortConfig.key === "voterNumber"}
                      direction={
                        sortConfig.key === "voterNumber"
                          ? sortConfig.direction
                          : "asc"
                      }
                      onClick={() => handleSort("voterNumber")}
                    >
                      Numéro votant
                    </TableSortLabel>
                  </TableCell>

                  <TableCell sx={{ fontWeight: "bold" }}>
                    <TableSortLabel
                      active={sortConfig.key === "playerName"}
                      direction={
                        sortConfig.key === "playerName"
                          ? sortConfig.direction
                          : "asc"
                      }
                      onClick={() => handleSort("playerName")}
                    >
                      Joueur
                    </TableSortLabel>
                  </TableCell>

                  <TableCell sx={{ fontWeight: "bold" }}>
                    <TableSortLabel
                      active={sortConfig.key === "playerNumber"}
                      direction={
                        sortConfig.key === "playerNumber"
                          ? sortConfig.direction
                          : "asc"
                      }
                      onClick={() => handleSort("playerNumber")}
                    >
                      Numéro
                    </TableSortLabel>
                  </TableCell>

                  <TableCell sx={{ fontWeight: "bold" }}>
                    <TableSortLabel
                      active={sortConfig.key === "matchDate"}
                      direction={
                        sortConfig.key === "matchDate"
                          ? sortConfig.direction
                          : "desc"
                      }
                      onClick={() => handleSort("matchDate")}
                    >
                      Match & Date
                    </TableSortLabel>
                  </TableCell>

                  <TableCell sx={{ fontWeight: "bold" }}>
                    <TableSortLabel
                      active={sortConfig.key === "date"}
                      direction={
                        sortConfig.key === "date"
                          ? sortConfig.direction
                          : "desc"
                      }
                      onClick={() => handleSort("date")}
                    >
                      Date du vote
                    </TableSortLabel>
                  </TableCell>

                  <TableCell sx={{ fontWeight: "bold" }}>
                    <TableSortLabel
                      active={sortConfig.key === "isActive"}
                      direction={
                        sortConfig.key === "isActive"
                          ? sortConfig.direction
                          : "desc"
                      }
                      onClick={() => handleSort("isActive")}
                    >
                      Statut
                    </TableSortLabel>
                  </TableCell>

                  <TableCell sx={{ fontWeight: "bold" }}>
                    <TableSortLabel
                      active={sortConfig.key === "isCoachVote"}
                      direction={
                        sortConfig.key === "isCoachVote"
                          ? sortConfig.direction
                          : "desc"
                      }
                      onClick={() => handleSort("isCoachVote")}
                    >
                      Type de vote
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedVotes.map((vote, index) => (
                  <TableRow
                    key={vote._id}
                    hover
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: "#fafafa",
                      },
                    }}
                  >
                    <TableCell>{vote.voterNumber}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {vote.playerName || "N/A"}
                    </TableCell>
                    <TableCell>{vote.playerNumber || "N/A"}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, fontSize: "0.875rem" }}
                        >
                          {vote.match.team1} vs {vote.match.team2}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {formatDate(vote.match.date)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(vote.date)}</TableCell>
                    <TableCell>
                      <Chip
                        label={vote.isActive ? "Actif" : "Inactif"}
                        color={vote.isActive ? "success" : "error"}
                        size="small"
                        variant="filled"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={vote.isCoachVote ? "Coach" : "Public"}
                        color={vote.isCoachVote ? "info" : "warning"}
                        size="small"
                        variant="filled"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={sortedVotes.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Lignes par page:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
              }
            />
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default VotesDashboard;
